import { createSelectors } from '@/store/constructor/createSelectors';
import { createStore } from '@/store/constructor/createStore';

import { getGroupDropdownList, GetGroupDropdownListRequest, getOperatorDropdownList, GetOperatorDropdownListParams, getWorkstationDropdownList, GetWorkstationDropdownListParams, TimeRangeType } from '../api';
import { GroupOptionItem, OperatorOptionItem, WorkstationOptionItem } from '../defined';

type State = {
  // group dropdown list
  lastHourGroupDropdownListLoading: boolean;
  lastHourGroupDropdownList: GroupOptionItem[];
  lastHourGroupDropdownListMap: Record<number, string>;
  lastTenHoursGroupDropdownListLoading: boolean;
  lastTenHoursGroupDropdownList: GroupOptionItem[];
  lastTenHoursGroupIdMap: Record<number, string>;
  // workstation dropdown list
  lastHourWorkstationDropdownListLoading: boolean;
  lastHourWorkstationDropdownList: WorkstationOptionItem[];
  lastHourWorkstationDropdownListMap: Record<string, string>;
  lastTenHoursWorkstationDropdownListLoading: boolean;
  lastTenHoursWorkstationDropdownList: WorkstationOptionItem[];
  lastTenHoursWorkstationDropdownListMap: Record<string, string>;
  // operator dropdown list
  lastHourOperatorDropdownListLoading: boolean;
  lastHourOperatorDropdownList: OperatorOptionItem[];
  lastHourOperatorDropdownListMap: Record<string, string>;
  lastTenHoursOperatorDropdownListLoading: boolean;
  lastTenHoursOperatorDropdownList: OperatorOptionItem[];
  lastTenHoursOperatorDropdownListMap: Record<string, string>;
}

type Action = {
  // group dropdown list
  getLastHourGroupDropdownList: () => GroupOptionItem[];
  setLastHourGroupDropdownList: (list: GroupOptionItem[], partial?: boolean) => void;
  getLastHourGroupDropdownListAsync: (params: GetGroupDropdownListRequest) => Promise<void>;
  getLastTenHoursGroupDropdownList: () => GroupOptionItem[];
  setLastTenHoursGroupDropdownList: (list: GroupOptionItem[], partial?: boolean) => void;
  getLastTenHoursGroupDropdownListAsync: (params: GetGroupDropdownListRequest) => Promise<void>;
  // workstation dropdown list
  setLastHourWorkstationDropdownList: (list: WorkstationOptionItem[], partial?: boolean) => void;
  getLastHourWorkstationDropdownList: () => WorkstationOptionItem[];
  getLastHourWorkstationDropdownListAsync: (params: GetWorkstationDropdownListParams) => Promise<void>;
  setLastTenHoursWorkstationDropdownList: (list: WorkstationOptionItem[], partial?: boolean) => void;
  getLastTenHoursWorkstationDropdownList: () => WorkstationOptionItem[];
  getLastTenHoursWorkstationDropdownListAsync: (params: GetWorkstationDropdownListParams) => Promise<void>;
  // operator dropdown list
  setLastHourOperatorDropdownList: (list: OperatorOptionItem[], partial?: boolean) => void;
  getLastHourOperatorDropdownList: () => OperatorOptionItem[];
  getLastHourOperatorDropdownListAsync: (params: GetOperatorDropdownListParams) => Promise<void>;
  setLastTenHoursOperatorDropdownList: (list: OperatorOptionItem[], partial?: boolean) => void;
  getLastTenHoursOperatorDropdownList: () => OperatorOptionItem[];
  getLastTenHoursOperatorDropdownListAsync: (params: GetOperatorDropdownListParams) => Promise<void>;
}

const useDropdownListStore = createSelectors(
  createStore<State & Action>((set, get) => {
    return {
      lastHourGroupDropdownList: [],
      lastHourGroupDropdownListLoading: false,
      lastHourGroupDropdownListMap: {},
      lastTenHoursGroupDropdownList: [],
      lastTenHoursGroupDropdownListLoading: false,
      lastTenHoursGroupIdMap: {},
      // workstation dropdown list
      lastHourWorkstationDropdownList: [],
      lastHourWorkstationDropdownListMap: {},
      lastHourWorkstationDropdownListLoading: false,
      lastTenHoursWorkstationDropdownList: [],
      lastTenHoursWorkstationDropdownListMap: {},
      lastTenHoursWorkstationDropdownListLoading: false,
      // operator dropdown list
      lastHourOperatorDropdownList: [],
      lastHourOperatorDropdownListMap: {},
      lastHourOperatorDropdownListLoading: false,
      lastTenHoursOperatorDropdownList: [],
      lastTenHoursOperatorDropdownListMap: {},
      lastTenHoursOperatorDropdownListLoading: false,
      getLastHourGroupDropdownListAsync: async (params) => {
        try {
          set({ lastHourGroupDropdownListLoading: true });
          const res = await getGroupDropdownList(params);
          const list = res.data?.list || [];
          const groupDropdownList = list.map((item) => ({
            label: item.workstation_group || '',
            value: item.workstation_group_id || 0,
          }));
          const groupDropdownListMap = list.reduce((acc, item) => {
            if (item.workstation_group_id) {
              acc[item.workstation_group_id] = item.workstation_group || '';
            }
            return acc;
          }, {} as Record<number, string>);
          set({
            lastHourGroupDropdownList: groupDropdownList,
            lastHourGroupDropdownListMap: groupDropdownListMap
          });
        } catch (error) {
        } finally {
          set({ lastHourGroupDropdownListLoading: false });
        }
      },
      getLastHourGroupDropdownList: () => get().lastHourGroupDropdownList || [],
      setLastHourGroupDropdownList: (list, partial = false) => {
        set({
          lastHourGroupDropdownList: partial ? [...get().lastHourGroupDropdownList, ...list] : list,
        });
      },
      getLastTenHoursGroupDropdownList: () => get().lastTenHoursGroupDropdownList || [],
      setLastTenHoursGroupDropdownList: (list, partial = false) => {
        set({
          lastTenHoursGroupDropdownList: partial ? [...get().lastTenHoursGroupDropdownList, ...list] : list,
        });
      },
      getLastTenHoursGroupDropdownListAsync: async (params) => {
        try {
          set({ lastTenHoursGroupDropdownListLoading: true });
          const res = await getGroupDropdownList(params);
          const list = res.data?.list || [];
          const groupDropdownList = list.map((item) => ({
            label: item.workstation_group || '',
            value: item.workstation_group_id || 0,
          }));
          set({
            lastTenHoursGroupDropdownList: groupDropdownList,
            lastTenHoursGroupIdMap: list.reduce((acc, item) => {
              // need to update with item.workstation_group
              if (item.workstation_group_id) {
                acc[item.workstation_group_id] = item.workstation_group || '';
              }
              return acc;
            }, {} as Record<number, string>),
          });
        } catch (error) {
        } finally {
          set({ lastTenHoursGroupDropdownListLoading: false });
        }
      },
      setLastHourWorkstationDropdownList: (list, partial = false) => {
        set({
          lastHourWorkstationDropdownList: partial ? [...get().lastHourWorkstationDropdownList, ...list] : list,
        });
      },
      getLastHourWorkstationDropdownList: () => get().lastHourWorkstationDropdownList || [],
      getLastHourWorkstationDropdownListAsync: async (params) => {
        try {
          set({ lastHourWorkstationDropdownListLoading: true });
          const defaultParams = { time_range_type: TimeRangeType.LastHour };
          const res = await getWorkstationDropdownList({ ...defaultParams, ...params });
          const list = res.data?.list || [];
          const workstationDropdownList = list.map((item) => ({
            label: `[${item.biz_workstation_id}] ${item.workstation_group_name}`,
            value: item.biz_workstation_id || '',
          }));
          set({
            lastHourWorkstationDropdownList: workstationDropdownList,
            lastHourWorkstationDropdownListMap: list.reduce((acc, item) => {
              if (item.biz_workstation_id) {
                acc[item.biz_workstation_id] = `[${item.biz_workstation_id}]${item.workstation_group_name}`;
              }
              return acc;
            }, {} as Record<string, string>),
          });
        } catch (error) {
        } finally {
          set({ lastHourWorkstationDropdownListLoading: false });
        }
      },
      getLastTenHoursWorkstationDropdownList: () => get().lastTenHoursWorkstationDropdownList || [],
      setLastTenHoursWorkstationDropdownList: (list, partial = false) => {
        set({
          lastTenHoursWorkstationDropdownList: partial ? [...get().lastTenHoursWorkstationDropdownList, ...list] : list,
        });
      },
      getLastTenHoursWorkstationDropdownListAsync: async (params) => {
        try {
          set({ lastTenHoursWorkstationDropdownListLoading: true });
          const defaultParams = { time_range_type: TimeRangeType.Last10Hours };
          const res = await getWorkstationDropdownList({ ...defaultParams, ...params });
          const list = res.data?.list || [];
          const workstationDropdownList = list.map((item) => ({
            label: `[${item.biz_workstation_id}] ${item.workstation_group_name}`,
            value: item.biz_workstation_id || '',
          }));
          const workstationDropdownListMap = list.reduce((acc, item) => {
            if (item.biz_workstation_id) {
              acc[item.biz_workstation_id] = `[${item.biz_workstation_id}]${item.workstation_group_name}`;
            }
            return acc;
          }, {} as Record<string, string>);
          set({
            lastTenHoursWorkstationDropdownList: workstationDropdownList,
            lastTenHoursWorkstationDropdownListMap: workstationDropdownListMap,
          });
        } catch (error) {
          console.error(error);
        } finally {
          set({ lastTenHoursWorkstationDropdownListLoading: false });
        }
      },
      setLastHourOperatorDropdownList: (list, partial = false) => {
        set({
          lastHourOperatorDropdownList: partial ? [...get().lastHourOperatorDropdownList, ...list] : list,
        });
      },
      getLastHourOperatorDropdownList: () => get().lastHourOperatorDropdownList || [],
      getLastHourOperatorDropdownListAsync: async (params) => {
        try {
          set({ lastHourOperatorDropdownListLoading: true });
          const defaultParams = { time_range_type: TimeRangeType.LastHour };
          const res = await getOperatorDropdownList({ ...defaultParams, ...params });
          const list = res.data?.list || [];
          const operatorDropdownList = list.map((item) => ({
            label: `[${item.ops_id}] ${item.ops_name}`,
            value: item.ops_id || '',
          }));
          const operatorDropdownListMap = list.reduce((acc, item) => {
            if (item.ops_id) {
              acc[item.ops_id] = `[${item.ops_id}]${item.ops_name}`;
            }
            return acc;
          }, {} as Record<string, string>);
          set({
            lastHourOperatorDropdownList: operatorDropdownList,
            lastHourOperatorDropdownListMap: operatorDropdownListMap,
          });
        } catch (error) {
          console.error(error);
        } finally {
          set({ lastHourOperatorDropdownListLoading: false });
        }
      },
      getLastTenHoursOperatorDropdownList: () => get().lastTenHoursOperatorDropdownList || [],
      setLastTenHoursOperatorDropdownList: (list, partial = false) => {
        set({
          lastTenHoursOperatorDropdownList: partial ? [...get().lastTenHoursOperatorDropdownList, ...list] : list,
        });
      },
      getLastTenHoursOperatorDropdownListAsync: async (params) => {
        try {
          set({ lastTenHoursOperatorDropdownListLoading: true });
          const defaultParams = { time_range_type: TimeRangeType.Last10Hours };
          const res = await getOperatorDropdownList({ ...defaultParams, ...params });
          const list = res.data?.list || [];
          const operatorDropdownList = list.map((item) => ({
            label: `[${item.ops_id}] ${item.ops_name}`,
            value: item.ops_id || '',
          }));
          const operatorDropdownListMap = list.reduce((acc, item) => {
            if (item.ops_id) {
              acc[item.ops_id] = `[${item.ops_id}]${item.ops_name}`;
            }
            return acc;
          }, {} as Record<string, string>);
          set({
            lastTenHoursOperatorDropdownList: operatorDropdownList,
            lastTenHoursOperatorDropdownListMap: operatorDropdownListMap,
          });
        } catch (error) {
          console.error(error);
        } finally {
          set({ lastTenHoursOperatorDropdownListLoading: false });
        }
      },
    };
  }),
);

export default useDropdownListStore;
